import React from 'react';
import { Building2, Home, Users2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Services = () => {
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.2 });
  const [cardsRef, cardsVisible] = useScrollAnimation({ threshold: 0.1 });
  const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.2 });

  const services = [
    {
      icon: Building2,
      title: "Private Sector Properties",
      description: "Expert consultation on commercial and private sector property developments, investments, and portfolio management.",
      image: "https://images.unsplash.com/photo-1771258129535-2004b0005603?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGJ1aWxkaW5nc3xlbnwwfHx8fDE3NzM5NDQ3NTl8MA&ixlib=rb-4.1.0&q=85",
      features: [
        "Market Analysis & Valuation",
        "Investment Strategy",
        "Property Portfolio Review",
        "Due Diligence Support"
      ]
    },
    {
      icon: Users2,
      title: "Social Housing Consultancy",
      description: "Specialized guidance on social housing developments, compliance, and management strategies for housing associations and developers.",
      image: "https://images.unsplash.com/photo-1759104050206-e5ea8dc7ee83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGJ1aWxkaW5nc3xlbnwwfHx8fDE3NzM5NDQ3NTl8MA&ixlib=rb-4.1.0&q=85",
      features: [
        "Regulatory Compliance",
        "Development Planning",
        "Funding & Grant Applications",
        "Community Impact Assessment"
      ]
    },
    {
      icon: Home,
      title: "Residential Property Advice",
      description: "Comprehensive residential property consultation for agents, covering sales, lettings, and property management strategies.",
      image: "https://images.unsplash.com/photo-1770839112216-d2522edf0d3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxyZXNpZGVudGlhbCUyMHByb3BlcnR5fGVufDB8fHx8MTc3Mzk0NDc3OXww&ixlib=rb-4.1.0&q=85",
      features: [
        "Sales & Lettings Strategy",
        "Property Valuation",
        "Market Positioning",
        "Client Advisory Services"
      ]
    }
  ];

  return (
    <section id="services" className="py-20 lg:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div ref={titleRef} className={`text-center mb-16 scroll-animate ${titleVisible ? 'visible' : ''}`}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a3a5c] mb-4">
              Our Specialist Services
            </h2>
            <div className="w-24 h-1 bg-[hsl(210,45%,45%)] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive property consultancy solutions tailored for real estate professionals seeking expert guidance and strategic insights.
            </p>
          </div>

          {/* Services Grid */}
          <div ref={cardsRef} className={`grid lg:grid-cols-3 gap-8 scroll-animate-stagger ${cardsVisible ? 'visible' : ''}`}>
            {services.map((service, index) => (
              <Card 
                key={index}
                className="group hover:shadow-2xl transition-all duration-300 border-2 border-[hsl(210,15%,93%)] shadow-md overflow-hidden rounded-none"
              >
                {/* Service Icon Header */}
                <div className="relative h-40 bg-gradient-to-br from-[hsl(215,65%,22%)] to-[hsl(210,45%,45%)] flex items-center justify-center">
                  <div className="bg-white/15 backdrop-blur-sm p-8">
                    <service.icon className="text-white" size={56} />
                  </div>
                </div>

                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-[hsl(215,65%,22%)] mb-4 group-hover:text-[hsl(210,45%,45%)] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <CheckCircle2 className="text-[hsl(210,45%,45%)] flex-shrink-0 mt-1" size={20} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Info */}
          <div ref={ctaRef} className={`mt-16 bg-gradient-to-r from-[hsl(215,65%,22%)] to-[hsl(210,45%,45%)] rounded-2xl p-8 sm:p-12 text-center text-white scroll-animate-scale ${ctaVisible ? 'visible' : ''}`}>
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">
              Need Tailored Advice for Your Clients?
            </h3>
            <p className="text-lg mb-6 max-w-2xl mx-auto text-gray-100">
              Our expert consultants are ready to provide you with strategic insights and professional guidance to help you excel in the property market.
            </p>
            <button 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-[hsl(215,65%,22%)] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 shadow-lg"
            >
              Schedule a Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
